import { useEffect, useState } from "react";
import Talk from "talkjs";
import { mapToTalkUser } from "../utils/TalkjsUtils";
import { useGetUser } from "./useGetUser";
import * as Sentry from "@sentry/react";

type ReturnType = {
  isChatLoading: boolean;
  session: Talk.Session | undefined;
  setInbox: React.Dispatch<React.SetStateAction<Talk.Inbox | undefined>>;
  setParticipants: React.Dispatch<any>;
  createConversation: (
    conversationId?: string,
    conversationSubject?: string
  ) => void;
  autoCreateChat: boolean;
  setAutoCreateChat: React.Dispatch<React.SetStateAction<boolean>>;
  unreadMessages: number;
};
export const useTalk = (userId: string): ReturnType => {
  const [talkLoaded, markTalkLoaded] = useState(false);
  const [participants, setParticipants] = useState<Array<Talk.User>>([]);
  const [session, setSession] = useState<Talk.Session>();
  const [inbox, setInbox] = useState<Talk.Inbox>();
  const { getUserResponse, isUserLoading } = useGetUser(userId);
  const [isLoading, setIsLoading] = useState(true);
  const [autoCreateChat, setAutoCreateChat] = useState(false);
  const [unreadMessages, setUnreadMessages] = useState<number>(0);

  useEffect(() => {
    Talk.ready.then(() => markTalkLoaded(true));
  }, [userId]);

  useEffect(() => {
    try {
      if (talkLoaded && getUserResponse) {
        const talkSession = new Talk.Session({
          appId: process.env.REACT_APP_TALKJS_ID!,
          signature: getUserResponse.getChatSignature().toUpperCase(),
          me: mapToTalkUser(getUserResponse),
        });
        setSession(talkSession);
        setIsLoading(false);
        talkSession.unreads.onChange((messages) => {
          setUnreadMessages(messages.length);
        });
        return () => talkSession.destroy();
      }
    } catch (error) {
        Sentry.captureException(error);
}
  }, [getUserResponse, talkLoaded]);

  const createConversation = (
    conversationId?: string,
    conversationSubject?: string
  ) => {
    try {
      if (talkLoaded && getUserResponse && session && inbox) {
        setIsLoading(true);
        const talkCurrentUser = mapToTalkUser(getUserResponse);
        if (!conversationId) {
          const predictableConversationId: string = [
            ...participants,
            talkCurrentUser,
          ]
            .map((u) => u.id)
            .sort((a, b) => a.localeCompare(b)) // Sort the IDs in ascending order
            .join(); // Join them into a single string
          conversationId = btoa(unescape(encodeURIComponent(predictableConversationId)));
        }
        const conversation = session.getOrCreateConversation(conversationId);
        conversation.subject = conversationSubject;
        participants.forEach((participant) => {
          conversation.setParticipant(participant);
        })
        const currentUserExist = participants.some((p) => p.id === talkCurrentUser.id);
        if (!currentUserExist) {
          conversation.setParticipant(talkCurrentUser);
        }
        inbox.select(conversation);
        setIsLoading(false);
      }
    } catch (error) {
        Sentry.captureException(error);
}
  };

  useEffect(() => {
    let usr = "";
    let totalParticipants: Talk.User[] | undefined = [];
    inbox?.onConversationSelected((event: Talk.ConversationSelectedEvent) => {
      totalParticipants = event.participants?.filter((p) => p.id !== userId);
      if (totalParticipants?.length === 1) usr = totalParticipants[0].name;
    });
    inbox?.onCustomConversationAction(
      "goToProfile",
      (event: Talk.ConversationActionEvent) => {
        if (usr !== "" && totalParticipants?.length === 1) {
          window.location.href = `/user-profile/${usr}`;
        }
      }
    );
  }, [inbox, userId]);

  return {
    isChatLoading: isLoading || isUserLoading,
    session,
    setInbox,
    setParticipants,
    createConversation,
    autoCreateChat,
    setAutoCreateChat,
    unreadMessages,
  };
};
